import { supabase } from '@/integrations/supabase/client';

export interface UserExportData {
  exportDate: string;
  userId: string;
  profile?: unknown;
  trainingSessions?: unknown[];
  userTechniques?: unknown[];
  gamePlans?: unknown[];
  createdTechniques?: unknown[];
  weeklyReports?: unknown[];
  error?: string;
}

export interface AccountDeletionRequest {
  password?: string;
  confirmationText: string;
}

export interface AccountDeletionResponse {
  success: boolean;
  message: string;
  error?: string;
}

/**
 * Service for handling account deletion operations
 */
export class AccountDeletionService {
  /**
   * Delete user account with proper validation and cleanup
   */
  static async deleteAccount(request: AccountDeletionRequest): Promise<AccountDeletionResponse> {
    try {
      // Validate confirmation text
      if (request.confirmationText !== 'DELETE') {
        return {
          success: false,
          message: '',
          error: 'Invalid confirmation text. Please type "DELETE" to confirm.'
        };
      }

      // Call the Supabase edge function
      const { data, error } = await supabase.functions.invoke('delete-account', {
        body: JSON.stringify(request)
      });

      if (error) {
        console.error('Account deletion error:', error);
        return {
          success: false,
          message: '',
          error: error.message || 'Failed to delete account'
        };
      }

      return data as AccountDeletionResponse;
    } catch (error) {
      console.error('Account deletion service error:', error);
      return {
        success: false,
        message: '',
        error: error instanceof Error ? error.message : 'Account deletion failed'
      };
    }
  }

  /**
   * Check if user has active subscriptions that would prevent deletion
   */
  static async checkSubscriptionStatus(): Promise<{ hasActiveSubscription: boolean; error?: string }> {
    try {
      // Use the existing permissions check to determine subscription status
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return { hasActiveSubscription: false, error: 'User not authenticated' };
      }

      // Call the check-permissions function to get current subscription status
      const { data, error } = await supabase.functions.invoke('check-permissions');

      if (error) {
        console.error('Subscription check error:', error);
        return { hasActiveSubscription: false, error: 'Failed to check subscription status' };
      }

      return { hasActiveSubscription: data?.isPremium || false };
    } catch (error) {
      console.error('Subscription status check error:', error);
      return { hasActiveSubscription: false, error: 'Failed to check subscription status' };
    }
  }

  /**
   * Export user data before deletion
   */
  static async exportUserData(): Promise<{ success: boolean; data?: UserExportData; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      // Collect user data from various tables
      const userData = await this.collectUserData(user.id);
      
      return { success: true, data: userData };
    } catch (error) {
      console.error('Data export error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Data export failed' 
      };
    }
  }

  /**
   * Collect all user data for export
   */
  private static async collectUserData(userId: string): Promise<UserExportData> {
    const userData: UserExportData = {
      exportDate: new Date().toISOString(),
      userId: userId
    };

    try {
      // Get profile data
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      userData.profile = profile;

      // Get training sessions
      const { data: trainingSessions } = await supabase
        .from('training_sessions')
        .select('*')
        .eq('user_id', userId)
        .order('date', { ascending: false });
      
      userData.trainingSessions = trainingSessions;

      // Get user techniques
      const { data: userTechniques } = await supabase
        .from('user_techniques')
        .select(`
          *,
          techniques (
            name,
            description,
            technique_type,
            difficulty
          )
        `)
        .eq('user_id', userId);
      
      userData.userTechniques = userTechniques;

      // Get game plans
      const { data: gamePlans } = await supabase
        .from('game_plans')
        .select('*')
        .eq('user_id', userId);
      
      userData.gamePlans = gamePlans;

      // Get created techniques
      const { data: createdTechniques } = await supabase
        .from('techniques')
        .select('*')
        .eq('created_by_user_id', userId);
      
      userData.createdTechniques = createdTechniques;

      // Get weekly reports
      const { data: weeklyReports } = await supabase
        .from('weekly_reports')
        .select('*')
        .eq('user_id', userId)
        .order('week_start', { ascending: false });
      
      userData.weeklyReports = weeklyReports;

    } catch (error) {
      console.error('Error collecting user data:', error);
      userData.error = 'Some data could not be collected';
    }

    return userData;
  }

  /**
   * Download user data as JSON file
   */
  static downloadUserData(userData: UserExportData, filename?: string): boolean {
    try {
      const dataStr = JSON.stringify(userData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `the-mat-monster-data-export-${new Date().toISOString().split('T')[0]}.json`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('Error downloading user data:', error);
      return false;
    }
  }

  /**
   * Get account deletion requirements and warnings
   */
  static getAccountDeletionInfo() {
    return {
      requirements: [
        'Cancel any active subscriptions',
        'Confirm your identity with password or OAuth',
        'Type "DELETE" to confirm the action'
      ],
      consequences: [
        'Your profile and personal information will be permanently removed',
        'All training journal entries and notes will be deleted',
        'Your saved techniques and game plans will be removed',
        'Access to premium features will be cancelled',
        'Your account will be removed from our systems within 30 days'
      ],
      preservation: [
        'Techniques you created and shared may remain available to other users',
        'Content you shared in public areas may be preserved for community benefit'
      ],
      timeline: 'Account deletion is processed immediately and cannot be undone'
    };
  }
}
